import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Globe, Check, AlertCircle, Loader2, X } from 'lucide-react';
import { api, ApiError, type DomainConnectState } from '../api/client';
import { useToast } from '../components/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/cn';

interface Props {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onPromoted: () => void;
}

// Phase 3 — copy + ordering for each connect-domain FSM step. We render
// a step list so the user sees progress through the multi-minute flow
// instead of a single opaque spinner. `description` is the active-state
// caption; the bullet steps render in this exact order.
//
// Migration 0014 added two post-reachability verify steps. The site is
// already serving at the custom domain during those steps — they're
// observational, but the user benefits from seeing why we're still
// spinning (the engine is checking IndexNow / Google / SF outcomes and
// auto-recovering where it can).
const PROGRESS_STEPS: Array<{
  state: Exclude<DomainConnectState, 'live' | 'failed'>;
  label: string;
}> = [
  { state: 'attaching', label: 'Attaching to Cloudflare Pages' },
  { state: 'dns_provisioning', label: 'Provisioning DNS' },
  { state: 'cert_pending', label: 'Waiting for certificate' },
  { state: 'building', label: 'Building site' },
  { state: 'deploying', label: 'Deploying' },
  { state: 'reachability_check', label: 'Verifying site is reachable' },
  { state: 'indexing_verify', label: 'Verifying search engine submission' },
  { state: 'audit_verify', label: 'Running quality audit' },
];

function stepIndex(state: DomainConnectState | undefined): number {
  if (!state || state === 'live' || state === 'failed') return -1;
  return PROGRESS_STEPS.findIndex((s) => s.state === state);
}

export function PromoteDialog({ projectId, open, onClose, onPromoted }: Props) {
  const toast = useToast();
  const [selected, setSelected] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'pick' | 'progress' | 'done' | 'failed'>('pick');
  const [connectedDomain, setConnectedDomain] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['availableDomains'],
    queryFn: () => api.availableDomains(),
    enabled: open && step === 'pick',
  });
  const domains = data?.domains ?? [];

  // Phase 3 — once the connect-domain POST returns 202, switch to polling
  // /state every 2s and read project.domainConnectState. We exit the
  // polling loop when the FSM reaches 'live' (success) or 'failed' (error).
  const { data: projectState } = useQuery({
    queryKey: ['projectState', projectId],
    queryFn: () => api.getProjectState(projectId),
    enabled: open && step === 'progress',
    refetchInterval: (q) => {
      const cs = q.state.data?.project.domainConnectState;
      if (cs === 'live' || cs === 'failed') return false;
      return 2000;
    },
  });

  const connectState = projectState?.project.domainConnectState;
  const connectError = projectState?.project.domainConnectError;
  const connectWarnings = projectState?.project.domainConnectWarnings ?? [];

  // Transition into the success/failure terminal screens when the engine
  // FSM lands. The 'done' transition also fires the onPromoted callback so
  // the parent page refreshes its data.
  useEffect(() => {
    if (step !== 'progress') return;
    if (connectState === 'live') {
      setStep('done');
      onPromoted();
    } else if (connectState === 'failed') {
      setStep('failed');
    }
  }, [step, connectState, onPromoted]);

  async function onConnect(): Promise<void> {
    if (!selected) {
      toast.error('Pick a domain first');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.connectDomain(projectId, selected);
      setConnectedDomain(r.rootDomain);
      setStep('progress');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      setSelected('');
      setStep('pick');
      setConnectedDomain(null);
      onClose();
    }
  }

  const activeIdx = stepIndex(connectState);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {step === 'pick' && (
          <>
            <DialogHeader>
              <DialogTitle>Connect a custom domain</DialogTitle>
              <DialogDescription>
                Pick a domain you own to make this site live on the web. We'll
                rebuild metadata and publish in ~5 minutes.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {isLoading && (
                <div className="flex items-center gap-2 text-[13px] text-zinc-500 py-6 justify-center">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading your domains…
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <div>
                    Couldn't load domains:{' '}
                    {error instanceof Error ? error.message : String(error)}
                  </div>
                </div>
              )}

              {!isLoading && !error && domains.length === 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-900">
                  No available domains yet. Add a domain in Cloudflare and run
                  the sync from the Domains tab.
                </div>
              )}

              {!isLoading && domains.length > 0 && (
                <div className="flex flex-col gap-1.5 max-h-72 overflow-y-auto -mx-1 px-1">
                  {domains.map((d) => {
                    const active = selected === d.id;
                    return (
                      <label
                        key={d.id}
                        className={cn(
                          'flex items-center gap-3 rounded-lg border px-3.5 py-3 cursor-pointer transition-colors',
                          active
                            ? 'border-zinc-900 bg-zinc-50'
                            : 'border-zinc-200 hover:bg-zinc-50',
                        )}
                      >
                        <input
                          type="radio"
                          name="managedDomain"
                          value={d.id}
                          checked={active}
                          onChange={() => setSelected(d.id)}
                          className="sr-only"
                        />
                        <div
                          className={cn(
                            'h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors',
                            active ? 'border-zinc-900' : 'border-zinc-300',
                          )}
                        >
                          {active && (
                            <span className="h-1.5 w-1.5 rounded-full bg-zinc-900" />
                          )}
                        </div>
                        <Globe className="h-3.5 w-3.5 text-zinc-400" />
                        <span className="font-mono text-[13px] flex-1 truncate">
                          {d.rootDomain}
                        </span>
                        {d.kind === 'agency' && (
                          <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                            agency
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={onConnect}
                loading={submitting}
                disabled={!selected || domains.length === 0}
              >
                Connect domain
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'progress' && (
          <>
            {/* Phase 3 — always-enabled close button. The flow keeps running
                in the engine even if the user steps away; reopening the
                project shows them the live URL when it's ready. */}
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              aria-label="Close (the connection will continue in the background)"
            >
              <X className="h-4 w-4" />
            </button>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-9 w-9 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 text-blue-700 animate-spin" />
                </div>
                <DialogTitle>Connecting {connectedDomain ?? 'domain'}…</DialogTitle>
              </div>
              <DialogDescription>
                This usually takes about 5 minutes. You can close this dialog
                and come back — we'll keep the process running.
              </DialogDescription>
            </DialogHeader>

            <ul className="space-y-2 my-2">
              {PROGRESS_STEPS.map((s, i) => {
                const done = activeIdx > i;
                const active = activeIdx === i;
                const pending = activeIdx < i;
                return (
                  <li
                    key={s.state}
                    className={cn(
                      'flex items-center gap-3 text-[13px]',
                      done && 'text-zinc-900',
                      active && 'text-zinc-900 font-medium',
                      pending && 'text-zinc-400',
                    )}
                  >
                    <span
                      className={cn(
                        'h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0',
                        done && 'border-emerald-500 bg-emerald-500',
                        active && 'border-blue-500',
                        pending && 'border-zinc-300',
                      )}
                    >
                      {done && <Check className="h-2.5 w-2.5 text-white" />}
                      {active && (
                        <Loader2 className="h-2.5 w-2.5 text-blue-500 animate-spin" />
                      )}
                    </span>
                    <span>
                      {s.label}
                      {active && '…'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {step === 'done' && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-9 w-9 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                  <Check className="h-4 w-4 text-emerald-700" />
                </div>
                <DialogTitle>Live</DialogTitle>
              </div>
              <DialogDescription>
                {connectedDomain ? (
                  <>
                    <span className="font-mono">{connectedDomain}</span> is now
                    serving the new content. Refresh this page to see the
                    updated dashboard.
                  </>
                ) : (
                  <>
                    Your domain is now serving the new content. Refresh this
                    page to see the updated dashboard.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {connectWarnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900 space-y-1.5">
                <div className="flex items-center gap-1.5 font-medium">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>Verification notes</span>
                </div>
                <ul className="list-disc list-outside ml-5 space-y-1">
                  {connectWarnings.map((w, i) => (
                    <li key={i} className="break-words">
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button onClick={onClose}>Got it</Button>
            </DialogFooter>
          </>
        )}

        {step === 'failed' && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-9 w-9 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 text-red-700" />
                </div>
                <DialogTitle>Connection failed</DialogTitle>
              </div>
              <DialogDescription>
                We couldn't finish connecting{' '}
                {connectedDomain ? (
                  <span className="font-mono">{connectedDomain}</span>
                ) : (
                  'your domain'
                )}
                . The engine recorded this reason:
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-800 font-mono whitespace-pre-wrap break-words">
              {connectError ?? 'Unknown error — check the engine logs.'}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button onClick={() => setStep('pick')}>Try again</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
